import _                                  from 'underscore';
import React, { FC, useEffect, useState } from 'react';
import { useHistory }                     from 'react-router-dom';
import { List, Segment }                  from 'semantic-ui-react';
import { useAuth0 }                       from "@auth0/auth0-react";

import { Align, CorvalUIColor, CorvalUtilHexColors }                                      from '@src/types';
import { RouterUtility }                                                                  from '@src/util';
import { useLastActivityTimestamp, useLastRedirectURL, useLogin, useSystemToasts }        from '@src/hooks';
import { CONSTANTS, ENVIRONMENT_IS_PRODUCTION }                                           from '@src/constants';
import { CenteredContentContainer, Layout, LayoutNames, Page, PageMeta, StyledContainer } from '@src/views/containers';
import { CorvalLogo, CorvalLogoVersions }                                                 from '@src/views/components/product/CorvalLogo';
import { CorvalStylizedHexContainer }                                                     from '@src/views/components/product/CorvalStylizedHexContainer';
import { CorvalCopyright }                                                                from '@src/views/components/product/CorvalCopyright';
import { CorvalVersionNumber }                                                            from "@src/views/components/product/CorvalVersionNumber";
import { AuthZeroLoginForm }                                                              from './AuthZeroLoginForm';
import { CorvalBranchName }                                                               from "@src/views/components/product/CorvalBranchName";


const LoginPage: FC = ( props ) => {

    let history                                                          = useHistory(),
        toastActions                                                     = useSystemToasts(),
        { getAuthenticatedCorvalUser }                                   = useLogin(),
        { setLastActivityTimestamp, clearLastActivityTimestamp }         = useLastActivityTimestamp(),
        { setLastRedirectURL, getLastRedirectURL, clearLastRedirectURL } = useLastRedirectURL(),
        AuthZeroContext                                                  = useAuth0();

    const [ userValidationPhaseReady, setUserValidationPhaseReady ] = useState( false );

    useEffect( () => {

        if( !getLastRedirectURL() ){

            let defaultRoute = RouterUtility.getRoutePath( 'DASHBOARD' ) as string,
                redirectUrl  = RouterUtility.getRedirectURLorRoute( defaultRoute );

            // console.log( { redirectUrl } );
            setLastRedirectURL( redirectUrl );
        }

    }, [] );

    useEffect( () => {

        if( AuthZeroContext.isAuthenticated && !userValidationPhaseReady ){

            setUserValidationPhaseReady( true );

        } else {

            // clear any activity timestamps available
            clearLastActivityTimestamp();
        }

    }, [ AuthZeroContext.isAuthenticated ] )

    useEffect( () => {

        if( userValidationPhaseReady ){

            // check the user to ensure we have an account ready for them.
            validateCorvalUser();

        }

    }, [ userValidationPhaseReady ] )

    let validateCorvalUser = async () => {

        try{

            // refetch user once auth is updated and test if it doesn't throw an error
            await getAuthenticatedCorvalUser();

            // redirect to an entry point
            handleRedirectToEntry();
            removeRedirectOnRouteTransition();

            console.log( `[SESSION]: User has Corval account.` );

        } catch( e ) {

            console.log( `[SESSION]: User does NOT have Corval account!` );

            // if we had an error...
            toastActions.error( 'Corval Account Unknown', 'The user you have logged in with does not seem to have a Corval account setup!', { timeoutMS: 0 } );

            handleRedirectToNoAccountPage();
            removeRedirectOnRouteTransition();
        }

    }

    let handleRedirectToEntry = () => {

        // get stored redirect
        let redirect = getLastRedirectURL() ?? RouterUtility.getRoutePath( 'DASHBOARD' ) as string;

        history.push( redirect );
    }

    let handleRedirectToNoAccountPage = () => {

        history.push( RouterUtility.getRoutePath( 'NO_ACCOUNT' ) as string );
    }

    let handleAuthZeroSuccess = async ( accessToken: string ) => {

        // Did we get an auth token or a challenge?
        if( _.isString( accessToken ) ){

        }
    };

    let removeRedirectOnRouteTransition = () => {

        // remove redirect annotation, but give time for the router to actually transition
        return setTimeout( clearLastRedirectURL, CONSTANTS.UI.INPUT.INACTIVE_DELAY );
    }

    return (

        <Page name={ 'login' }
              className={ 'bg--colored-blue' }
              padMainHorizontal={ false }
              padMainVertical={ false }
              showTop={ false }
              showNav={ false }
              monitorUserActivity={ false }
              showToggleMode={ false }
              ignoreDisplaySettings={ true }
              fadeIn>

            <CorvalStylizedHexContainer inverted fill={ 'area' } fadeColor={ CorvalUtilHexColors.DARK_BLUE } fadeDirection={ 'down' }>

                <PageMeta title={ 'Login' }>

                    <Layout name={ LayoutNames.FOCUSED_CENTERED } textAlign={ Align.LEFT }>

                        <Segment raised>

                            <div className={ 'padding-lg-horizontal padding-md-vertical' }>

                                <CorvalLogo type={ CorvalLogoVersions.BLUE_TEXT_COLORIZED } wrapped className={ 'margin-lg-bottom' }/>

                                {/* Auth0 Support */ }
                                <AuthZeroLoginForm onLoginSuccess={ handleAuthZeroSuccess }/>

                            </div>

                        </Segment>

                        <CenteredContentContainer>
                            <StyledContainer textColor={ CorvalUIColor.WHITE }>

                                <List inverted horizontal size={ 'tiny' } divided className={ 'text--colored-lt-screen-50' }>
                                    <List.Item><CorvalCopyright/></List.Item>
                                    <List.Item><CorvalVersionNumber/></List.Item>
                                    { !ENVIRONMENT_IS_PRODUCTION && <List.Item><CorvalBranchName/></List.Item> }
                                </List>

                            </StyledContainer>
                        </CenteredContentContainer>

                    </Layout>

                </PageMeta>

            </CorvalStylizedHexContainer>

        </Page>
    );
};

export default LoginPage;
